import { useMemo, type ReactNode } from 'react'
import { Monitor, Sun, Moon } from 'lucide-react'
import { useTheme } from '@/components/theme-provider'

type Theme = 'light' | 'dark' | 'system'

interface ThemeOption {
  icon: ReactNode
  value: Theme
  label: string
}

const THEME_LIST: ThemeOption[] = [
  {
    icon: <Monitor className="h-4 w-4" />,
    value: 'system',
    label: 'Auto',
  },
  {
    icon: <Sun className="h-4 w-4" />,
    value: 'light',
    label: 'Light',
  },
  {
    icon: <Moon className="h-4 w-4" />,
    value: 'dark',
    label: 'Dark',
  },
]

export function ThemeToggle() {
  const { theme, setTheme } = useTheme()

  const themeSelectClasses = useMemo(() => {
    if (theme === 'dark') {
      return 'left-[58px]'
    } else if (theme === 'light') {
      return 'left-[30px]'
    } else {
      return 'left-0.5'
    }
  }, [theme])

  return (
    <div
      className="bg-muted text-muted-foreground relative flex items-center rounded-full p-0.5"
      role="radiogroup"
      aria-label="Theme selector"
    >
      <div
        className={`bg-background absolute top-0.5 h-7 w-7 rounded-full shadow-sm transition-all duration-200 ease-out ${themeSelectClasses}`}
        aria-hidden="true"
      />
      {THEME_LIST.map((item) => (
        <div
          className={`relative inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ${item.value === theme ? 'text-foreground' : 'text-muted-foreground/75 hover:text-foreground'}`}
          key={item.value}
          onClick={() => setTheme(item.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setTheme(item.value)
            }
          }}
          tabIndex={0}
          role="radio"
          aria-label={item.label}
          aria-checked={item.value === theme}
        >
          {item.icon}
        </div>
      ))}
    </div>
  )
}
